import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Tag, 
  Modal,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Drawer,
  Descriptions,
  Timeline,
  Badge,
  Tooltip,
  Collapse,
  Switch
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  EyeOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  DownloadOutlined,
  ReloadOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import './SystemActivityLogs.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;
const { Panel } = Collapse;

// Mock system activity data - comprehensive admin operations
const mockSystemActivities = [
  {
    id: 'activity_20241223_015',
    timestamp: '2024-12-23T16:45:32Z',
    adminUserId: 'admin_sarah_johnson',
    adminEmail: 'sarah.johnson@lithlan.com',
    adminName: 'Sarah Johnson',
    
    action: 'create',
    resourceType: 'taxCategories',
    resourceId: 'VAPE_DISPOSABLE_HIGH_NICOTINE',
    resourceName: 'Disposable High Nicotine Vapes',
    
    changes: {
      before: null,
      after: {
        name: 'Disposable High Nicotine Vapes',
        pathString: 'Vaping Products > Disposable Vapes > High Nicotine (>20mg/ml)',
        parentCategoryId: 'VAPE_DISPOSABLE',
        description: 'Disposable vaping devices with nicotine content exceeding 20mg/ml',
        keywords: ['disposable', 'vape', 'high nicotine', 'pod', 'throwaway'],
        ageRestriction: '21+',
        licensingRequirements: ['tobacco_retailer_license'],
        regulatoryFlags: ['FDA_regulated'],
        isActive: true
      }
    },
    
    reason: 'New regulatory classification needed for high-nicotine disposable products',
    sessionId: 'session_admin_20241223_001',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    impact: 'low',
    status: 'completed'
  },
  
  {
    id: 'activity_20241223_014',
    timestamp: '2024-12-23T16:30:15Z',
    adminUserId: 'admin_mike_chen',
    adminEmail: 'mike.chen@lithlan.com',
    adminName: 'Mike Chen',
    
    action: 'update',
    resourceType: 'TaxRules',
    resourceId: 'excise_ca_cigarettes_luxury',
    resourceName: 'California Luxury Cigarette Tax',
    
    changes: {
      before: {
        rate: 1.87,
        effectiveDate: '2024-01-01T00:00:00Z',
        description: 'California luxury cigarette excise tax for premium brands'
      },
      after: {
        rate: 2.15,
        effectiveDate: '2024-12-23T00:00:00Z',
        description: 'California luxury cigarette excise tax for premium brands - Updated for 2024 Q4 rate increase'
      }
    },
    
    reason: 'Rate increase per California Department of Tax and Fee Administration notice',
    sessionId: 'session_admin_20241223_002',
    ipAddress: '10.0.0.45',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15',
    impact: 'high',
    status: 'completed'
  },
  
  {
    id: 'activity_20241223_013',
    timestamp: '2024-12-23T15:22:48Z',
    adminUserId: 'admin_sarah_johnson',
    adminEmail: 'sarah.johnson@lithlan.com',
    adminName: 'Sarah Johnson',
    
    action: 'delete',
    resourceType: 'jurisdictions',
    resourceId: 'US_TX_201_75001_OBSOLETE',
    resourceName: 'Dallas County (Obsolete ZIP)',
    
    changes: {
      before: {
        zipCode: '75001',
        city: 'Addison',
        county: 'Dallas',
        state: 'TX',
        salesTaxRates: {
          state: 6.25,
          county: 0.5,
          city: 1.0,
          total: 7.75
        },
        isActive: false,
        note: 'ZIP code consolidated into 75254'
      },
      after: null
    },
    
    reason: 'Cleanup of obsolete ZIP code after postal service consolidation',
    sessionId: 'session_admin_20241223_001',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    impact: 'low',
    status: 'completed'
  },
  
  {
    id: 'activity_20241223_012',
    timestamp: '2024-12-23T14:45:12Z',
    adminUserId: 'admin_alex_rodriguez',
    adminEmail: 'alex.rodriguez@lithlan.com',
    adminName: 'Alex Rodriguez',
    
    action: 'update',
    resourceType: 'salesTaxRules',
    resourceId: 'sales_rule_ny_statewide',
    resourceName: 'New York State Sales Tax',
    
    changes: {
      before: {
        baseRate: 8.0,
        categoryRules: [
          {
            categoryCode: 'clothing',
            exemptions: [
              {
                condition: 'item_price_under',
                value: 110.00,
                exemptionType: 'full'
              }
            ]
          }
        ]
      },
      after: {
        baseRate: 8.0,
        categoryRules: [
          {
            categoryCode: 'clothing',
            exemptions: [
              {
                condition: 'item_price_under',
                value: 110.00,
                exemptionType: 'full'
              }
            ]
          },
          {
            categoryCode: 'footwear',
            exemptions: [
              {
                condition: 'item_price_under',
                value: 110.00,
                exemptionType: 'full'
              }
            ]
          }
        ]
      }
    },
    
    reason: 'Added footwear exemption matching clothing exemption rules',
    sessionId: 'session_admin_20241223_003',
    ipAddress: '172.16.0.25',
    userAgent: 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36',
    impact: 'medium',
    status: 'completed'
  },
  
  {
    id: 'activity_20241223_011',
    timestamp: '2024-12-23T13:15:30Z',
    adminUserId: 'admin_mike_chen',
    adminEmail: 'mike.chen@lithlan.com',
    adminName: 'Mike Chen',
    
    action: 'create',
    resourceType: 'TaxRules',
    resourceId: 'excise_federal_firearms_pistols',
    resourceName: 'Federal Excise Tax - Pistols and Revolvers',
    
    changes: {
      before: null,
      after: {
        name: 'Federal Excise Tax - Pistols and Revolvers',
        taxCategoryId: 'FIREARMS_HANDGUNS',
        jurisdiction: {
          country: 'US',
          state: null,
          counties: [],
          cities: []
        },
        calculationMethod: 'ad_valorem',
        rate: 10.0,
        unit: '%',
        applicationPoint: 'manufacturer',
        effectiveDate: '2024-01-01T00:00:00Z',
        isActive: true
      }
    },
    
    reason: 'Implementation of federal firearms excise tax regulations',
    sessionId: 'session_admin_20241223_002',
    ipAddress: '10.0.0.45',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15',
    impact: 'high',
    status: 'completed'
  },
  
  {
    id: 'activity_20241223_010',
    timestamp: '2024-12-23T12:30:45Z',
    adminUserId: 'admin_sarah_johnson',
    adminEmail: 'sarah.johnson@lithlan.com',
    adminName: 'Sarah Johnson',
    
    action: 'update',
    resourceType: 'taxCalculationConfig',
    resourceId: 'global_tax_config',
    resourceName: 'Global Tax Calculation Configuration',
    
    changes: {
      before: {
        calculationOrder: {
          method: 'excise_first_then_sales'
        },
        exciseTaxConfig: {
          'TOBACCO_CIGARETTES': {
            shippingTreatment: {
              mustPrepay: true,
              requiresStamps: true
            }
          }
        }
      },
      after: {
        calculationOrder: {
          method: 'excise_first_then_sales'
        },
        exciseTaxConfig: {
          'TOBACCO_CIGARETTES': {
            shippingTreatment: {
              mustPrepay: true,
              requiresStamps: true,
              shippingTaxMethod: 'destination_based'
            }
          },
          'FIREARMS_HANDGUNS': {
            calculationMethod: 'ad_valorem',
            applicationPoint: 'manufacturer',
            includeInSalesTaxBase: true,
            displayMethod: 'separate'
          }
        }
      }
    },
    
    reason: 'Added firearms tax configuration and updated shipping tax methods',
    sessionId: 'session_admin_20241223_001',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    impact: 'high',
    status: 'completed'
  },
  
  {
    id: 'activity_20241223_009',
    timestamp: '2024-12-23T11:45:20Z',
    adminUserId: 'admin_lisa_wang',
    adminEmail: 'lisa.wang@lithlan.com',
    adminName: 'Lisa Wang',
    
    action: 'archive',
    resourceType: 'taxCategories',
    resourceId: 'TOBACCO_PIPE_VINTAGE',
    resourceName: 'Vintage Pipe Tobacco (Discontinued)',
    
    changes: {
      before: {
        isActive: true,
        name: 'Vintage Pipe Tobacco (Discontinued)',
        pathString: 'Tobacco Products > Pipe Tobacco > Vintage',
        description: 'Discontinued vintage pipe tobacco products'
      },
      after: {
        isActive: false,
        name: 'Vintage Pipe Tobacco (Discontinued)',
        pathString: 'Tobacco Products > Pipe Tobacco > Vintage',
        description: 'Discontinued vintage pipe tobacco products - ARCHIVED',
        archivedAt: '2024-12-23T11:45:20Z',
        archivedBy: 'admin_lisa_wang'
      }
    },
    
    reason: 'Category no longer in use - no active products in this classification',
    sessionId: 'session_admin_20241223_004',
    ipAddress: '203.0.113.15',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    impact: 'low',
    status: 'completed'
  }
];

// Activity statistics
const activityStats = {
  totalActivities: 127,
  todayActivities: 7,
  activeAdmins: 4,
  recentErrors: 0,
  topAction: 'update',
  topResource: 'TaxRules'
};

function SystemActivityLogs() {
  const [activities, setActivities] = useState(mockSystemActivities);
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    adminUser: '',
    action: '',
    resourceType: '',
    dateRange: null,
    impact: '',
    search: ''
  });

  const getActionColor = (action) => {
    const colors = {
      create: 'green',
      update: 'blue',
      delete: 'red',
      archive: 'orange',
      restore: 'cyan'
    };
    return colors[action] || 'default';
  };

  const getImpactColor = (impact) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red'
    };
    return colors[impact] || 'default';
  };

  const getResourceTypeIcon = (resourceType) => {
    const icons = {
      taxCategories: '📂',
      TaxRules: '⚖️',
      salesTaxRules: '🗺️',
      jurisdictions: '🌍',
      taxCalculationConfig: '⚙️'
    };
    return icons[resourceType] || '📄';
  };

  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setDrawerVisible(true);
  };

  const exportActivities = () => {
    // Mock CSV export
    const csvContent = activities.map(activity => [
      activity.timestamp,
      activity.adminName,
      activity.action,
      activity.resourceType,
      activity.resourceName,
      activity.impact,
      activity.reason
    ].join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => new Date(timestamp).toLocaleString(),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      width: 180,
    },
    {
      title: 'Admin User',
      key: 'admin',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{record.adminName}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.adminEmail}
            </Text>
          </div>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag color={getActionColor(action)} style={{ textTransform: 'uppercase' }}>
          {action}
        </Tag>
      ),
      filters: [
        { text: 'Create', value: 'create' },
        { text: 'Update', value: 'update' },
        { text: 'Delete', value: 'delete' },
        { text: 'Archive', value: 'archive' },
      ],
      onFilter: (value, record) => record.action === value,
      width: 100,
    },
    {
      title: 'Resource',
      key: 'resource',
      render: (_, record) => (
        <div>
          <div>
            <span style={{ marginRight: 8 }}>
              {getResourceTypeIcon(record.resourceType)}
            </span>
            {record.resourceType}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.resourceName}
          </Text>
        </div>
      ),
      filters: [
        { text: 'Tax Categories', value: 'taxCategories' },
        { text: 'Tax Rules', value: 'TaxRules' },
        { text: 'Sales Tax Rules', value: 'salesTaxRules' },
        { text: 'Jurisdictions', value: 'jurisdictions' },
        { text: 'Tax Config', value: 'taxCalculationConfig' },
      ],
      onFilter: (value, record) => record.resourceType === value,
      width: 250,
    },
    {
      title: 'Impact',
      dataIndex: 'impact',
      key: 'impact',
      render: (impact) => (
        <Badge 
          status={impact === 'high' ? 'error' : impact === 'medium' ? 'warning' : 'success'} 
          text={impact.toUpperCase()} 
        />
      ),
      filters: [
        { text: 'Low', value: 'low' },
        { text: 'Medium', value: 'medium' },
        { text: 'High', value: 'high' },
      ],
      onFilter: (value, record) => record.impact === value,
      width: 100,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      width: 300,
    },
    {
      title: 'Action',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          size="small"
        >
          Details
        </Button>
      ),
      width: 100,
    },
  ];

  const renderChangeDetails = (changes) => {
    if (!changes) return null;

    return (
      <Collapse defaultActiveKey={['changes']}>
        <Panel header="Change Details" key="changes">
          <Row gutter={16}>
            {changes.before && (
              <Col span={12}>
                <Card size="small" title="Before" type="inner">
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(changes.before, null, 2)}
                  </pre>
                </Card>
              </Col>
            )}
            {changes.after && (
              <Col span={changes.before ? 12 : 24}>
                <Card size="small" title="After" type="inner">
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(changes.after, null, 2)}
                  </pre>
                </Card>
              </Col>
            )}
          </Row>
        </Panel>
      </Collapse>
    );
  };

  return (
    <div className="system-activity-logs">
      <div className="page-header">
        <Title level={2}>
          <HistoryOutlined /> System Activity Logs
        </Title>
        <Text type="secondary">
          Administrative operations audit and change tracking
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Activities"
              value={activityStats.totalActivities}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Today's Activities"
              value={activityStats.todayActivities}
              prefix={<Badge status="processing" />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Admins"
              value={activityStats.activeAdmins}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Recent Errors"
              value={activityStats.recentErrors}
              valueStyle={{ color: activityStats.recentErrors > 0 ? '#cf1322' : '#3f8600' }}
              prefix={<Badge status={activityStats.recentErrors > 0 ? 'error' : 'success'} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder="Search activities..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Admin User"
              style={{ width: '100%' }}
              value={filters.adminUser}
              onChange={(value) => setFilters({ ...filters, adminUser: value })}
              allowClear
            >
              <Option value="admin_sarah_johnson">Sarah Johnson</Option>
              <Option value="admin_mike_chen">Mike Chen</Option>
              <Option value="admin_alex_rodriguez">Alex Rodriguez</Option>
              <Option value="admin_lisa_wang">Lisa Wang</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Action Type"
              style={{ width: '100%' }}
              value={filters.action}
              onChange={(value) => setFilters({ ...filters, action: value })}
              allowClear
            >
              <Option value="create">Create</Option>
              <Option value="update">Update</Option>
              <Option value="delete">Delete</Option>
              <Option value="archive">Archive</Option>
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            />
          </Col>
          <Col span={4}>
            <Space>
              <Button icon={<ReloadOutlined />}>Refresh</Button>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={exportActivities}
              >
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Activities Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={activities}
          rowKey="id"
          loading={loading}
          pagination={{
            total: activities.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} activities`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Detail Drawer */}
      <Drawer
        title="Activity Details"
        width={800}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        extra={
          <Button 
            icon={<DownloadOutlined />} 
            size="small"
          >
            Export
          </Button>
        }
      >
        {selectedActivity && (
          <div>
            <Descriptions title="Activity Information" bordered column={2}>
              <Descriptions.Item label="Activity ID" span={2}>
                {selectedActivity.id}
              </Descriptions.Item>
              <Descriptions.Item label="Timestamp">
                {new Date(selectedActivity.timestamp).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Admin User">
                {selectedActivity.adminName} ({selectedActivity.adminEmail})
              </Descriptions.Item>
              <Descriptions.Item label="Action">
                <Tag color={getActionColor(selectedActivity.action)}>
                  {selectedActivity.action.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Impact">
                <Badge 
                  status={selectedActivity.impact === 'high' ? 'error' : 
                           selectedActivity.impact === 'medium' ? 'warning' : 'success'} 
                  text={selectedActivity.impact.toUpperCase()} 
                />
              </Descriptions.Item>
              <Descriptions.Item label="Resource Type" span={2}>
                {getResourceTypeIcon(selectedActivity.resourceType)} {selectedActivity.resourceType}
              </Descriptions.Item>
              <Descriptions.Item label="Resource Name" span={2}>
                {selectedActivity.resourceName}
              </Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>
                {selectedActivity.reason}
              </Descriptions.Item>
              <Descriptions.Item label="Session ID" span={2}>
                {selectedActivity.sessionId}
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">
                {selectedActivity.ipAddress}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="green">{selectedActivity.status}</Tag>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 24 }}>
              {renderChangeDetails(selectedActivity.changes)}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default SystemActivityLogs;